import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { Row, Col } from 'reactstrap';
// Models
import { InventoryEntities } from 'client/data/models/inventory';
// Utils
import { numberWithCommas } from 'site-modules/shared/utils/string';
import { getPlural } from 'client/utils/plural';
import {
  getDeliveryText,
  getVehicleName,
  transformDerivedFeatures,
} from 'site-modules/shared/utils/inventory/usurp-inventory-card';
import {
  isDriveway,
  isFiveStarDealer as getIsFiveStarDealer,
  isPartnerDealer,
} from 'site-modules/shared/utils/dealer-details-utils';
import { generateDeepLink } from 'site-modules/shared/utils/car-buying-online/generate-deep-link';
import { EventToolbox } from 'client/utils/event-toolbox';
import {
  addAdditionalUTMParams,
  updateDealerWebsiteTrackingParams,
  getDealerWebsiteTrackingParams,
  getDealerWebsiteUrl,
} from 'client/site-modules/shared/utils/dealership-link-constructor';
import { getEngineAttr } from 'client/site-modules/shared/components/inventory/utils/get-engine-attr';
import { getEngineAttrIcon } from 'client/site-modules/shared/components/inventory/utils/get-engine-attr-icon';
import { isRecurrentAvailable } from 'site-modules/shared/utils/inventory/is-recurrent-available';
import {
  getRecurrentRangeValue,
  getRecurrentScoreValue,
} from 'site-modules/shared/components/usurp-inventory-card/usurp-inventory-card-helper';
import { getCta } from 'client/site-modules/car-buying-online/utils/get-cta';
// Constants
import { LOAN_PAYMENT } from 'site-modules/shared/constants/allowed-inventory-request-params';
import { ACTIONS } from 'client/engagement-handlers/inventory-engagement-handler/constants';
import { DRIVEWAY_DCM_CLICK_TAG } from 'site-modules/shared/constants/inventory/dcm-click-tag';
import { ELECTRIC_FUEL_TYPE } from 'site-modules/shared/constants/features-specs';
import { BODY_TYPES } from 'client/site-modules/shared/constants/inventory/body-types';
// Components
import { Link } from 'site-modules/shared/components/link/link';
import { TopFeatureBubbles } from 'site-modules/shared/components/inventory/top-feature-bubbles/top-feature-bubbles';
import { Button } from 'site-modules/shared/components/inventory/button/button';
import { UsurpInventoryCardInfo } from 'site-modules/shared/components/inventory/usurp-inventory-card-info/usurp-inventory-card-info';
import { VehicleNameDisplay } from 'site-modules/shared/components/inventory/vehicle-name-display/vehicle-name-display';
import { UsurpCardKeyPoint } from 'site-modules/shared/components/inventory/usurp-card-key-point/usurp-card-key-point';

const getKeyPoints = (vehicle, isElectric, isShowRecurrentData) => {
  const { vehicleInfo, dealerInfo } = vehicle;
  const mileage = vehicleInfo.mileage || 0;
  const isFiveStarDealer = getIsFiveStarDealer(vehicle);
  const deliveryText = getDeliveryText({ vehicle, isNational: true });
  const showElectricityRange = isElectric && !isShowRecurrentData;
  const engineAttrIcon = getEngineAttrIcon({ vehicleInfo, showElectricityRange });
  const engineAttr = getEngineAttr({ vehicleInfo, showElectricityRange });
  const recurrentRangeValue = getRecurrentRangeValue(vehicle);
  const recurrentScoreValue = getRecurrentScoreValue(vehicle);

  return [
    {
      icon: 'icon-meter',
      iconTitle: 'Car Mileage',
      content: mileage ? `${numberWithCommas(mileage)} ${getPlural('mile', mileage)}` : 'Not provided',
    },
    BODY_TYPES.includes(vehicleInfo.styleInfo?.bodyType) &&
      !isShowRecurrentData &&
      engineAttr && {
        icon: engineAttrIcon,
        iconTitle: 'Engine attribute',
        content: engineAttr,
      },
    isShowRecurrentData && {
      icon: 'icon-low-miles',
      iconTitle: 'Current Est. Range',
      content: recurrentRangeValue,
    },
    isShowRecurrentData && {
      icon: 'icon-battery_charging_full2',
      iconTitle: 'Battery Range Score',
      content: recurrentScoreValue,
    },
    {
      icon: 'icon-location',
      iconTitle: 'Dealer Location',
      content: dealerInfo.name || '',
    },
    deliveryText && {
      icon: 'icon-transit',
      iconTitle: 'Shipping',
      content: <Fragment>{deliveryText}*</Fragment>,
      showFiveStar: isFiveStarDealer,
    },
    !deliveryText &&
      isFiveStarDealer && {
        icon: 'icon-5star text-blue-50',
        content: <span className="size-12 fw-medium text-blue-50">Five Star Dealer</span>,
      },
  ].filter(el => !!el);
};

export const UsurpInventoryCardDescription = ({
  vehicle,
  zipCode = '',
  selectedPaymentType = '',
  includeDeliveryFee = false,
  ariaLabel = '',
  vdpLink,
  openLinksInNewTab = false,
}) => {
  const cgfFeatures = vehicle.vehicleInfo.partsInfo?.cgf || [];
  const parentDealershipName = vehicle.dealerInfo.displayInfo?.parentDealershipName;
  const isElectric = vehicle.vehicleInfo.partsInfo?.engineType === ELECTRIC_FUEL_TYPE;
  const deepLinkUrl = vehicle.deepLinkUrl || generateDeepLink(vehicle);

  const topFeatures = cgfFeatures && cgfFeatures.length ? cgfFeatures : transformDerivedFeatures(vehicle);
  const isPartner = isPartnerDealer(vehicle);
  const isDrivewayDealer = isDriveway(parentDealershipName);
  const isShowRecurrentData = isElectric && isRecurrentAvailable(vehicle);
  const isLoanCardWithLoanPayment = Boolean(selectedPaymentType === LOAN_PAYMENT && vehicle.prices?.loan?.payment);
  const [firstLine, secondLine] = getVehicleName(vehicle, true, true);
  const vehicleNameDisplay = <VehicleNameDisplay vehicle={vehicle} firstLine={firstLine} secondLine={secondLine} />;
  const keyPoints = getKeyPoints(vehicle, isElectric, isShowRecurrentData);

  const handleBolSrpCardEvent = () => {
    EventToolbox.fireCustomEvent(ACTIONS.BUY_ONLINE_SRP_CARD_BTN_CLICK, {
      partnerId: vehicle.dealerInfo.rooftopId || '',
    });
  };

  const dealerWebSiteUrl = getDealerWebsiteUrl(deepLinkUrl || '');
  const dealerTrackingParams = deepLinkUrl ? getDealerWebsiteTrackingParams(deepLinkUrl) : '';
  const dealerWebSiteTrackingParams = dealerWebSiteUrl
    ? updateDealerWebsiteTrackingParams({
        deepLinkUrl: dealerTrackingParams,
        dealer: parentDealershipName,
        zipCode,
        isUsedType: true,
      })
    : dealerTrackingParams;
  const linkWithAdditionalUTMParams = addAdditionalUTMParams({
    deepLink: `${dealerWebSiteUrl}${dealerWebSiteTrackingParams}`,
    dealer: parentDealershipName,
    isSRP: true,
  });
  return (
    <div className="visible-vehicle-info d-flex flex-column">
      <UsurpInventoryCardInfo
        vehicle={vehicle}
        selectedPaymentType={selectedPaymentType}
        includeDeliveryFee={includeDeliveryFee}
        ariaLabel={ariaLabel}
        vehicleNameDisplay={vehicleNameDisplay}
        vdpLink={vdpLink}
        isLoanCardWithLoanPayment={isLoanCardWithLoanPayment}
        openLinksInNewTab={openLinksInNewTab}
      />
      <Row tag="ul" className="list-unstyled mb-0">
        <UsurpCardKeyPoint tag={Col} columnSizes={{ xs: 12 }} keyPoints={keyPoints} htmlTag="li" isGrayIcons />
      </Row>
      <TopFeatureBubbles topFeatures={topFeatures} className="mt-1" />
      <div className="pt-1 margin-top-auto d-inline-block">
        <Button
          aria-label={`Buy Online ${ariaLabel}`}
          tag={Link}
          to={
            isDrivewayDealer && isPartner
              ? `${DRIVEWAY_DCM_CLICK_TAG}${linkWithAdditionalUTMParams}`
              : linkWithAdditionalUTMParams
          }
          size="sm"
          color="blue-50"
          className="px-1 text-capitalize fw-medium size-16 w-100 bol-btn"
          onClick={handleBolSrpCardEvent}
          rel="nofollow"
          target="_blank"
        >
          <span className="cta-text-wrapper h-100 d-inline-flex align-items-center justify-content-center">
            {getCta(parentDealershipName)}
            <span className="ms-0_25 icon-open_in_new small" aria-hidden />
          </span>
        </Button>
      </div>
    </div>
  );
};

UsurpInventoryCardDescription.propTypes = {
  vehicle: InventoryEntities.InventoryVin.isRequired,
  zipCode: PropTypes.string,
  selectedPaymentType: PropTypes.string,
  includeDeliveryFee: PropTypes.bool,
  ariaLabel: PropTypes.string,
  vdpLink: PropTypes.string.isRequired,
  openLinksInNewTab: PropTypes.bool,
};
